/* eslint-disable react/prop-types */
import * as React from 'react';

import { Node } from '@tiptap/core';
import { mergeAttributes } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import type { CharacterCountOptions } from '@tiptap/extension-character-count';
import Color from '@tiptap/extension-color';
import type { ColorOptions } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import type { HighlightOptions } from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import type { ImageOptions } from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import type { LinkOptions } from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import Placeholder from '@tiptap/extension-placeholder';
import type { PlaceholderOptions } from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import type { SubscriptExtensionOptions } from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import type { SuperscriptExtensionOptions } from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import type { TableOptions } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import type { TableCellOptions } from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import type { TableHeaderOptions } from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import type { TableRowOptions } from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import type { TaskItemOptions } from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import type { TaskListOptions } from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import type { TextAlignOptions } from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import type { TextStyleOptions } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import type { UnderlineOptions } from '@tiptap/extension-underline';
import { EditorContent as RichTextEditorContent } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { StarterKitOptions } from '@tiptap/starter-kit';
import { useBoolean, useMemoizedFn, useUpdateEffect } from 'ahooks';
import {
  Button,
  Col,
  ColorPicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';

import {
  AlignCenterOutlinedIcon,
  AlignLeftOutlinedIcon,
  AlignRightOutlinedIcon,
  AntdUpload,
  AppstoreOutlinedIcon,
  BoldOutlinedIcon,
  BorderBottomOutlinedIcon,
  BranchesOutlinedIcon,
  CheckOutlinedIcon,
  CheckSquareOutlinedIcon,
  ColumnWidthOutlinedIcon,
  DecimalInputNumber,
  EllipsisOutlinedIcon,
  FileSearchOutlinedIcon,
  FontColorsOutlinedIcon,
  FontSizeOutlinedIcon,
  GatewayOutlinedIcon,
  H1Icon,
  H2Icon,
  H3Icon,
  H4Icon,
  H5Icon,
  H6Icon,
  HighlightOutlinedIcon,
  ItalicOutlinedIcon,
  LinkOutlinedIcon,
  MenuOutlinedIcon,
  OrderedListOutlinedIcon,
  PicRightOutlinedIcon,
  PictureOutlinedIcon,
  PlayCircleOutlinedIcon,
  PlaySquareOutlinedIcon,
  PrimitiveForm,
  RedoOutlinedIcon,
  SecurityScanOutlinedIcon,
  StopOutlinedIcon,
  StrikethroughOutlinedIcon,
  SubscriptIcon,
  SuperscriptIcon,
  TableCellMergeIcon,
  TableCellSplitIcon,
  TableColumnInsertAfterIcon,
  TableColumnInsertBeforeIcon,
  TableColumnRemoveIcon,
  TableHeaderCellIcon,
  TableHeaderColumnIcon,
  TableHeaderRowIcon,
  TableIcon,
  TableRemoveIcon,
  TableRowInsertAfterIcon,
  TableRowInsertBeforeIcon,
  TableRowRemoveIcon,
  TextIcon,
  UnderlineOutlinedIcon,
  UndoOutlinedIcon,
  UnorderedListOutlinedIcon,
} from 'components';
import {
  RichTextEditorContext,
  useRichTextEditorAction,
  useRichTextEditorValue,
} from 'context';
import type { AntdUploadChangeParam, FileOwnerType } from 'types';
import {
  cn,
  createFileLocaleUnitSize,
  EDITOR,
  FILE,
  formatFileLocaleUnitSize,
  generateDownloadFile,
  generateDownloadThumbnail,
  getObjectValues,
  inputRule,
  KEYBOARD,
  LINK,
  PATHNAME,
  randomId,
} from 'utils';

const {
  TARGET: { DEFAULT: DEFAULT_TARGET, ...LINK_TARGET_OPTIONS },
} = LINK;
const {
  QUERY_PARAM: {
    DEFAULT: { SPACE: DEFAULT_SPACE, DOT: DEFAULT_DOT },
  },
} = PATHNAME;
const {
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
  VIDEO: {
    SIZE: { WIDTH: VIDEO_SIZE_WIDTH, HEIGHT: VIDEO_SIZE_HEIGHT },
  },
  AUDIO: {
    SIZE: { WIDTH: AUDIO_SIZE_WIDTH, HEIGHT: AUDIO_SIZE_HEIGHT },
  },
  IFRAME: {
    SIZE: { WIDTH: IFRAME_SIZE_WIDTH, HEIGHT: IFRAME_SIZE_HEIGHT },
  },
} = FILE;
const {
  SHORTCUT: {
    COMMAND: SHORTCUT_COMMAND,
    SHIFT: SHORTCUT_SHIFT,
    ALT: SHORTCUT_ALT,
    PLUS: SHORTCUT_PLUS,
  },
} = KEYBOARD;
const {
  TOOLBAR: {
    HEADING: TOOLBAR_HEADING,
    PARAGRAPH: TOOLBAR_PARAGRAPH,
    ORDERED_LIST: TOOLBAR_ORDERED_LIST,
    BULLET_LIST: TOOLBAR_BULLET_LIST,
    TASK_LIST: TOOLBAR_TASK_LIST,
    TABLE: TOOLBAR_TABLE,
    BLOCKQUOTE: TOOLBAR_BLOCKQUOTE,
    CODE_BLOCK: TOOLBAR_CODE_BLOCK,
    BOLD: TOOLBAR_BOLD,
    ITALIC: TOOLBAR_ITALIC,
    SUBSCRIPT: TOOLBAR_SUBSCRIPT,
    SUPERSCRIPT: TOOLBAR_SUPERSCRIPT,
    UNDERLINE: TOOLBAR_UNDERLINE,
    STRIKE: TOOLBAR_STRIKE,
    HIGHLIGHT: TOOLBAR_HIGHLIGHT,
    TEXT_STYLE: TOOLBAR_TEXT_STYLE,
    LINK: TOOLBAR_LINK,
    IMAGE: TOOLBAR_IMAGE,
    VIDEO: TOOLBAR_VIDEO,
    AUDIO: TOOLBAR_AUDIO,
    IFRAME: TOOLBAR_IFRAME,
    RIGHT: TOOLBAR_RIGHT,
    LEFT: TOOLBAR_LEFT,
    CENTER: TOOLBAR_CENTER,
    JUSTIFY: TOOLBAR_JUSTIFY,
    HORIZONTAL_RULE: TOOLBAR_HORIZONTAL_RULE,
  },
} = EDITOR;

type SelectOption = {
  key: string;
  label: React.ReactNode;
  options: SelectOptionItem[];
};

type SelectOptionItem = {
  key: string;
  value: SelectOptionValue;
  label: React.ReactNode;
  description?: React.ReactNode | React.ReactNode[];
  title?: string;
  icon?: React.ElementType;
  disabled?: boolean;
};

type SelectOptionValue = string | number;

type LinkFieldType = NonNullable<
  ReturnType<typeof useRichTextEditorValue>['linkAttributes']
>;

type ImageFieldType = Pick<
  Required<
    NonNullable<ReturnType<typeof useRichTextEditorValue>['imageAttributes']>
  >,
  'alt' | 'width' | 'height'
> &
  Record<'fileInfo', AntdUploadChangeParam>;

type VideoFieldType = Pick<
  Required<
    NonNullable<ReturnType<typeof useRichTextEditorValue>['videoAttributes']>
  >,
  'width' | 'height'
> &
  Record<'fileInfo', AntdUploadChangeParam>;

type AudioFieldType = Record<'fileInfo', AntdUploadChangeParam>;

type IframeFieldType = Required<
  NonNullable<ReturnType<typeof useRichTextEditorValue>['iframeAttributes']>
>;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      setVideo: (props: React.ComponentPropsWithoutRef<'video'>) => ReturnType;
    };
    audio: {
      setAudio: (props: React.ComponentPropsWithoutRef<'audio'>) => ReturnType;
    };
    iframe: {
      setIframe: (
        props: React.ComponentPropsWithoutRef<'iframe'>,
      ) => ReturnType;
    };
  }
}

interface VideoOptions {
  HTMLAttributes: Record<string, any>;
}

const Video = Node.create<VideoOptions>({
  name: TOOLBAR_VIDEO,
  group: 'block',
  selectable: true,
  draggable: true,
  atom: true,
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      src: { default: '' },
      width: { default: VIDEO_SIZE_WIDTH },
      height: { default: VIDEO_SIZE_HEIGHT },
      controls: { default: true },
      loop: { default: false },
      muted: { default: false },
      poster: { default: null },
      preload: { default: 'metadata' },
      playsinline: { default: false },
    };
  },
  addCommands() {
    return {
      setVideo:
        props =>
        ({ tr, dispatch }) => {
          const { selection } = tr;
          const node = this.type.create(props);

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node);
          }

          return true;
        },
    };
  },
  addNodeView() {
    return ({ node: { attrs } }) => {
      const {
        src,
        width,
        height,
        controls,
        autoplay,
        loop,
        muted,
        poster,
        preload,
        playsinline,
      } = attrs;
      const video = document.createElement('video');
      const source = document.createElement('source');

      video.src = src;
      video.width = width;
      video.height = height;
      if (controls) video.controls = true;
      if (autoplay) video.autoplay = true;
      if (loop) video.loop = true;
      if (muted) video.muted = true;
      if (poster) video.setAttribute('poster', poster);
      if (preload) video.setAttribute('preload', preload);
      if (playsinline) video.setAttribute('playsinline', '');
      source.src = src;
      source.type = 'video/mp4';

      video.append(source);
      return { dom: video };
    };
  },
  parseHTML() {
    return [
      {
        tag: TOOLBAR_VIDEO,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [TOOLBAR_VIDEO, mergeAttributes(HTMLAttributes)];
  },
});

interface AudioOptions {
  HTMLAttributes: Record<string, any>;
}

const Audio = Node.create<AudioOptions>({
  name: TOOLBAR_AUDIO,
  group: 'block',
  selectable: true,
  draggable: true,
  atom: true,
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      src: { default: '' },
      controls: { default: true },
      loop: { default: false },
      muted: { default: false },
      preload: { default: 'metadata' },
    };
  },
  addCommands() {
    return {
      setAudio:
        props =>
        ({ tr, dispatch }) => {
          const { selection } = tr;
          const node = this.type.create(props);

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node);
          }

          return true;
        },
    };
  },
  addNodeView() {
    return ({ node: { attrs } }) => {
      const { src, controls, autoplay, loop, muted, preload } = attrs;
      const audio = document.createElement('audio');

      audio.src = src;
      if (controls) audio.controls = true;
      if (autoplay) audio.autoplay = true;
      if (loop) audio.loop = true;
      if (muted) audio.muted = true;
      if (preload) audio.setAttribute('preload', preload);

      return { dom: audio };
    };
  },
  parseHTML() {
    return [
      {
        tag: TOOLBAR_AUDIO,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [TOOLBAR_AUDIO, mergeAttributes(HTMLAttributes)];
  },
});

interface IframeOptions {
  allowFullscreen: boolean;
  HTMLAttributes: Record<string, any>;
}

const Iframe = Node.create<IframeOptions>({
  name: TOOLBAR_IFRAME,
  group: 'block',
  selectable: true,
  draggable: true,
  atom: true,
  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      src: { default: '' },
      width: { default: IFRAME_SIZE_WIDTH },
      height: { default: IFRAME_SIZE_HEIGHT },
      allowfullscreen: { default: true },
    };
  },
  addCommands() {
    return {
      setIframe:
        props =>
        ({ tr, dispatch }) => {
          const { selection } = tr;
          const node = this.type.create(props);

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node);
          }

          return true;
        },
    };
  },
  parseHTML() {
    return [
      {
        tag: TOOLBAR_IFRAME,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [TOOLBAR_IFRAME, mergeAttributes(HTMLAttributes)];
  },
});

type RichTextEditorToolbarProps = StrictOmit<
  React.ComponentPropsWithoutRef<typeof Flex>,
  'children'
> &
  Record<'editor', NonNullable<ReturnType<typeof useEditor>>> &
  Pick<RichTextEditorProps, 'ownerType'>;

const RichTextEditorToolbar = React.forwardRef<
  React.ElementRef<typeof Flex>,
  RichTextEditorToolbarProps
>(({ editor, ownerType, vertical = true, ...props }, ref) => {
  const [isInlineTextExpand, { toggle: toggleInlineTextExpand }] = useBoolean();
  const [isListOptionsExpand, { toggle: toggleListOptionsExpand }] =
    useBoolean();
  const [isMediaContentExpand, { toggle: toggleMediaContentExpand }] =
    useBoolean();
  const [isTableItemsExpand, { toggle: toggleTableItemsExpand }] = useBoolean();
  const [isLinkModalOpen, { toggle: toggleLinkModalOpen }] = useBoolean();
  const [isImageModalOpen, { toggle: toggleImageModalOpen }] = useBoolean();
  const [isVideoModalOpen, { toggle: toggleVideoModalOpen }] = useBoolean();
  const [isAudioModalOpen, { toggle: toggleAudioModalOpen }] = useBoolean();
  const [isIframeModalOpen, { toggle: toggleIframeModalOpen }] = useBoolean();

  const [linkForm] = Form.useForm<LinkFieldType>();
  const [imageForm] = Form.useForm<ImageFieldType>();
  const [videoForm] = Form.useForm<VideoFieldType>();
  const [audioForm] = Form.useForm<AudioFieldType>();
  const [iframeForm] = Form.useForm<IframeFieldType>();

  const {
    linkAttributes,
    imageAttributes,
    videoAttributes,
    audioAttributes,
    iframeAttributes,
  } = useRichTextEditorValue();
  const richTextEditorAction = useRichTextEditorAction();

  const {
    href: initialLinkHref,
    target: initialLinkTarget,
    rel: initialLinkRel,
  } = editor.getAttributes(TOOLBAR_LINK);
  const {
    src: initialImageSrc,
    width: initialImageWidth,
    height: initialImageHeight,
    alt: initialImageAlt,
  } = editor.getAttributes(TOOLBAR_IMAGE);

  useUpdateEffect(() => {
    richTextEditorAction({
      type: 'UPDATE_RICH_TEXT_EDITOR_LINK_ATTRIBUTES',
      payload: {
        linkAttributes: {
          href: initialLinkHref,
          target: initialLinkTarget,
          rels: initialLinkRel
            ? initialLinkRel.split(DEFAULT_SPACE)
            : undefined,
        },
      },
    });
  }, [initialLinkHref, initialLinkTarget, initialLinkRel]);
  useUpdateEffect(() => {
    richTextEditorAction({
      type: 'UPDATE_RICH_TEXT_EDITOR_IMAGE_ATTRIBUTES',
      payload: {
        imageAttributes: {
          src: initialImageSrc,
          width: initialImageWidth,
          height: initialImageHeight,
          name: initialImageAlt,
          alt: initialImageAlt,
        },
      },
    });
  }, [initialImageSrc, initialImageWidth, initialImageHeight, initialImageAlt]);

  React.useEffect(() => {
    return () => {
      editor.destroy();
    };
  }, [editor]);

  const editorCan = useMemoizedFn(() => editor.can());
  const editorChainFocus = useMemoizedFn(() => editor.chain().focus());
  const editorCanChainFocus = useMemoizedFn(() => editorCan().chain().focus());

  const generateTooltipTitle = useMemoizedFn(
    (
      label: React.ReactNode,
      description?: React.ReactNode | React.ReactNode[],
    ) => {
      const descriptions = description
        ? Array.isArray(description)
          ? description
          : [description]
        : [];

      return (
        <Space
          className='text-center [&_*]:text-inherit'
          direction='vertical'
          size={4}
        >
          <div>
            <Typography.Text>{label}</Typography.Text>
          </div>
          {descriptions.length ? (
            <Space size={2} style={{ direction: 'ltr' }}>
              {descriptions.map((description, index) => {
                const id = randomId();
                const isFirstIndex = index === 0;

                return (
                  <React.Fragment key={id}>
                    {!isFirstIndex ? (
                      <Typography.Text>{SHORTCUT_PLUS}</Typography.Text>
                    ) : null}
                    <Typography.Text keyboard>{description}</Typography.Text>
                  </React.Fragment>
                );
              })}
            </Space>
          ) : null}
        </Space>
      );
    },
  );

  const blockTextOptions = React.useMemo<SelectOption[]>(
    () => [
      {
        key: randomId(),
        label: <span>تیترها</span>,
        options: [
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}1`,
            label: <span>تیتر اول</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 1],
            icon: H1Icon,
            disabled: true,
          },
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}2`,
            label: <span>تیتر دوم</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 2],
            icon: H2Icon,
          },
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}3`,
            label: <span>تیتر سوم</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 3],
            icon: H3Icon,
          },
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}4`,
            label: <span>تیتر چهارم</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 4],
            icon: H4Icon,
          },
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}5`,
            label: <span>تیتر پنجم</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 5],
            icon: H5Icon,
          },
          {
            key: randomId(),
            value: `${TOOLBAR_HEADING}${DEFAULT_DOT}6`,
            label: <span>تیتر ششم</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 6],
            icon: H6Icon,
          },
        ],
      },
      {
        key: randomId(),
        label: <span>توضیحات</span>,
        options: [
          {
            key: randomId(),
            value: TOOLBAR_PARAGRAPH,
            label: <span>پاراگراف</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 0],
            icon: FontSizeOutlinedIcon,
          },
          {
            key: randomId(),
            value: TOOLBAR_CODE_BLOCK,
            label: <span>نمونه کد</span>,
            description: [SHORTCUT_COMMAND, SHORTCUT_ALT, 'C'],
            icon: BranchesOutlinedIcon,
            disabled: true,
          },
        ],
      },
    ],
    [],
  );
  const textAlignOptions = React.useMemo<SelectOptionItem[]>(
    () => [
      {
        key: randomId(),
        value: TOOLBAR_RIGHT,
        label: <span>راست چین</span>,
        description: [SHORTCUT_COMMAND, SHORTCUT_SHIFT, 'R'],
        icon: AlignRightOutlinedIcon,
      },
      {
        key: randomId(),
        value: TOOLBAR_LEFT,
        label: <span>چپ چین</span>,
        description: [SHORTCUT_COMMAND, SHORTCUT_SHIFT, 'L'],
        icon: AlignLeftOutlinedIcon,
      },
      {
        key: randomId(),
        value: TOOLBAR_CENTER,
        label: <span>وسط چین</span>,
        description: [SHORTCUT_COMMAND, SHORTCUT_SHIFT, 'E'],
        icon: AlignCenterOutlinedIcon,
      },
      {
        key: randomId(),
        value: TOOLBAR_JUSTIFY,
        label: <span>تراز چین</span>,
        description: [SHORTCUT_COMMAND, SHORTCUT_SHIFT, 'J'],
        icon: MenuOutlinedIcon,
      },
    ],
    [],
  );
  const linkRelOptions = React.useMemo<SelectOption[]>(
    () => [
      {
        key: randomId(),
        label: <span>Security and Privacy</span>,
        options: [
          {
            key: randomId(),
            value: 'noopener',
            label: <span>noopener</span>,
            icon: SecurityScanOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'noreferrer',
            label: <span>noreferrer</span>,
            icon: SecurityScanOutlinedIcon,
          },
        ],
      },
      {
        key: randomId(),
        label: <span>SEO and Crawling</span>,
        options: [
          {
            key: randomId(),
            value: 'nofollow',
            label: <span>nofollow</span>,
            icon: FileSearchOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'ugc',
            label: <span>ugc</span>,
            icon: FileSearchOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'sponsored',
            label: <span>sponsored</span>,
            icon: FileSearchOutlinedIcon,
          },
        ],
      },
      {
        key: randomId(),
        label: <span>Link Relationship</span>,
        options: [
          {
            key: randomId(),
            value: 'alternate',
            label: <span>alternate</span>,
            icon: LinkOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'canonical',
            label: <span>canonical</span>,
            icon: LinkOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'prev',
            label: <span>prev</span>,
            icon: LinkOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'next',
            label: <span>next</span>,
            icon: LinkOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'author',
            label: <span>author</span>,
            icon: LinkOutlinedIcon,
          },
        ],
      },
      {
        key: randomId(),
        label: <span>Navigation and App Usage</span>,
        options: [
          {
            key: randomId(),
            value: 'bookmark',
            label: <span>bookmark</span>,
            icon: AppstoreOutlinedIcon,
          },
          {
            key: randomId(),
            value: 'tag',
            label: <span>tag</span>,
            icon: AppstoreOutlinedIcon,
          },
        ],
      },
    ],
    [],
  );

  const defaultSelectProps: React.ComponentPropsWithoutRef<
    typeof Select<SelectOptionValue>
  > = {
    variant: 'filled',
    className: 'min-w-[4.5rem]',
    popupMatchSelectWidth: false,
    menuItemSelectedIcon: <CheckOutlinedIcon className='ms-4 text-base' />,
    optionRender: ({ data, groupOption = false }) => {
      const {
        value,
        label,
        description,
        icon = StopOutlinedIcon,
        disabled = false,
      } = data as SelectOptionItem;
      const descriptions = description
        ? Array.isArray(description)
          ? description
          : [description]
        : [];

      return (
        <Space
          size={12}
          data-value={value}
          data-disabled={disabled}
          data-group-option={groupOption}
          className='flex data-[group-option=false]:ps-[--ant-control-padding-horizontal] [.ant-select-item-option-grouped:has(+.ant-select-item-option_&[data-group-option=false])]:mb-1'
        >
          <div>
            {React.createElement(icon, { className: 'size-5 *:size-full' })}
          </div>
          <Space className='[&_*]:text-inherit' direction='vertical' size={4}>
            <div>
              <Typography.Text>{label}</Typography.Text>
            </div>
            {descriptions.length ? (
              <Space
                size={2}
                className='*:font-normal'
                style={{ direction: 'ltr' }}
              >
                {descriptions.map((item, index) => {
                  const id = randomId();
                  const isFirstIndex = index === 0;

                  return (
                    <React.Fragment key={id}>
                      {!isFirstIndex ? (
                        <Typography.Text>{SHORTCUT_PLUS}</Typography.Text>
                      ) : null}
                      <Typography.Text keyboard>{item}</Typography.Text>
                    </React.Fragment>
                  );
                })}
              </Space>
            ) : null}
          </Space>
        </Space>
      );
    },
  };
  const defaultButtonProps: React.ComponentPropsWithoutRef<typeof Button> = {
    type: 'text',
    className:
      'size-8 min-w-[unset] data-[control-show=false]:hidden data-[active=true]:bg-[--ant-color-fill-tertiary] [&~[data-expanded=false]]:data-[control-expand]:absolute [&~[data-expanded=false]]:data-[control-expand]:-z-50 [&~[data-expanded=false]]:data-[control-expand]:scale-0',
  };
  const defaultModalProps: React.ComponentPropsWithoutRef<typeof Modal> = {
    centered: true,
    width: 640,
    okButtonProps: { autoFocus: true, htmlType: 'submit' },
  };

  const {
    href: linkHref,
    target: linkTarget,
    rels: linkRels,
  } = linkAttributes ?? {};
  const {
    id: imageId,
    src: imageSrc,
    name: imageName,
    alt: imageAlt,
    width: imageWidth = IMAGE_SIZE_WIDTH,
    height: imageHeight = IMAGE_SIZE_HEIGHT,
  } = imageAttributes ?? {};
  const {
    id: videoId,
    src: videoSrc,
    name: videoName,
    width: videoWidth = VIDEO_SIZE_WIDTH,
    height: videoHeight = VIDEO_SIZE_HEIGHT,
  } = videoAttributes ?? {};
  const { id: audioId, src: audioSrc, name: audioName } = audioAttributes ?? {};
  const {
    src: iframeSrc,
    width: iframeWidth = IFRAME_SIZE_WIDTH,
    height: iframeHeight = IFRAME_SIZE_HEIGHT,
  } = iframeAttributes ?? {};

  const {
    storage: {
      characterCount: { characters: charactersCounter, words: wordsCounter },
    },
  } = editor;
  const { characters, words } = {
    characters: charactersCounter(),
    words: wordsCounter(),
  };
  const hasImagePreview = !!(imageSrc || imageId);
  const hasVideoPreview = !!(videoSrc || videoId);
  const hasAudioPreview = !!(audioSrc || audioId);
  const maxImageUploadSize = createFileLocaleUnitSize(25);
  const maxVideoUploadSize = createFileLocaleUnitSize(50);
  const maxAudioUploadSize = createFileLocaleUnitSize(50);

  return (
    <Flex ref={ref} vertical={vertical} {...props}>
      <Flex
        align='center'
        className='gap-1 overflow-x-auto p-3 *:relative *:flex-initial *:gap-[inherit]'
      >
        <Flex>
          <Tooltip title={generateTooltipTitle('تیترها و توضیحات')}>
            <Select<SelectOptionValue>
              {...defaultSelectProps}
              placeholder={<TextIcon className='size-4' />}
              options={blockTextOptions}
              labelRender={({ value }) => {
                const selectedOptionItem = blockTextOptions
                  .flatMap(({ options }) => options)
                  .find(
                    option =>
                      JSON.stringify(option.value) === JSON.stringify(value),
                  );
                const { icon = StopOutlinedIcon } = selectedOptionItem ?? {};

                return React.createElement(icon, {
                  className: 'size-4 *:size-full',
                });
              }}
              value={
                editor.isActive(TOOLBAR_HEADING, { level: 1 })
                  ? `${TOOLBAR_HEADING}${DEFAULT_DOT}1`
                  : editor.isActive(TOOLBAR_HEADING, { level: 2 })
                    ? `${TOOLBAR_HEADING}${DEFAULT_DOT}2`
                    : editor.isActive(TOOLBAR_HEADING, { level: 3 })
                      ? `${TOOLBAR_HEADING}${DEFAULT_DOT}3`
                      : editor.isActive(TOOLBAR_HEADING, { level: 4 })
                        ? `${TOOLBAR_HEADING}${DEFAULT_DOT}4`
                        : editor.isActive(TOOLBAR_HEADING, { level: 5 })
                          ? `${TOOLBAR_HEADING}${DEFAULT_DOT}5`
                          : editor.isActive(TOOLBAR_HEADING, { level: 6 })
                            ? `${TOOLBAR_HEADING}${DEFAULT_DOT}6`
                            : editor.isActive(TOOLBAR_PARAGRAPH)
                              ? TOOLBAR_PARAGRAPH
                              : editor.isActive(TOOLBAR_CODE_BLOCK)
                                ? TOOLBAR_CODE_BLOCK
                                : undefined
              }
              onChange={value => {
                if (typeof value === 'string') {
                  const [group, option] = value.split(DEFAULT_DOT) as Partial<
                    [string, string]
                  >;

                  switch (group) {
                    case TOOLBAR_HEADING: {
                      const level = Number(option) as Parameters<
                        ReturnType<typeof editorChainFocus>['toggleHeading']
                      >[0]['level'];

                      if (isNaN(level)) return;

                      editorChainFocus().toggleHeading({ level }).run();
                      return;
                    }
                    case TOOLBAR_PARAGRAPH: {
                      editorChainFocus().setParagraph().run();
                      return;
                    }
                    case TOOLBAR_CODE_BLOCK: {
                      editorChainFocus().toggleCodeBlock().run();
                      return;
                    }
                    default: {
                      return;
                    }
                  }
                }
              }}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('چینش‌ها')}>
            <Select<SelectOptionValue>
              {...defaultSelectProps}
              placeholder={<ColumnWidthOutlinedIcon className='*:size-4' />}
              options={textAlignOptions}
              labelRender={({ value }) => {
                const selectedOptionItem = textAlignOptions.find(
                  option =>
                    JSON.stringify(option.value) === JSON.stringify(value),
                );
                const { icon = StopOutlinedIcon } = selectedOptionItem ?? {};

                return React.createElement(icon, {
                  className: 'size-4 *:size-full',
                });
              }}
              value={
                editor.isActive({ textAlign: TOOLBAR_RIGHT })
                  ? TOOLBAR_RIGHT
                  : editor.isActive({ textAlign: TOOLBAR_LEFT })
                    ? TOOLBAR_LEFT
                    : editor.isActive({ textAlign: TOOLBAR_CENTER })
                      ? TOOLBAR_CENTER
                      : editor.isActive({ textAlign: TOOLBAR_JUSTIFY })
                        ? TOOLBAR_JUSTIFY
                        : undefined
              }
              onChange={value => {
                if (typeof value === 'string') {
                  editorChainFocus().setTextAlign(value).run();
                }
              }}
            />
          </Tooltip>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <Tooltip
            title={generateTooltipTitle('ناانجام', [SHORTCUT_COMMAND, 'Z'])}
          >
            <Button
              {...defaultButtonProps}
              icon={<UndoOutlinedIcon />}
              disabled={!editorCanChainFocus().undo().run()}
              onClick={() => editorChainFocus().undo().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('بازانجام', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              'Z',
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<RedoOutlinedIcon />}
              disabled={!editorCanChainFocus().redo().run()}
              onClick={() => editorChainFocus().redo().run()}
            />
          </Tooltip>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <Tooltip
            title={generateTooltipTitle('کلمه پراهمیت', [
              SHORTCUT_COMMAND,
              'B',
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<BoldOutlinedIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_BOLD)}
              disabled={!editorCanChainFocus().toggleBold().run()}
              onClick={() => editorChainFocus().toggleBold().run()}
            />
          </Tooltip>
          <React.Fragment>
            <Button
              {...defaultButtonProps}
              type='link'
              data-control-expand='inline-text'
              data-control-show={!isInlineTextExpand}
              icon={<EllipsisOutlinedIcon />}
              onClick={toggleInlineTextExpand}
            />
          </React.Fragment>
          <Tooltip
            title={generateTooltipTitle('کلمه مورب', [SHORTCUT_COMMAND, 'I'])}
          >
            <Button
              {...defaultButtonProps}
              icon={<ItalicOutlinedIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_ITALIC)}
              disabled={!editorCanChainFocus().toggleItalic().run()}
              onClick={() => editorChainFocus().toggleItalic().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('کلمه زیرخط', [SHORTCUT_COMMAND, 'U'])}
          >
            <Button
              {...defaultButtonProps}
              icon={<UnderlineOutlinedIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_UNDERLINE)}
              disabled={!editorCanChainFocus().toggleUnderline().run()}
              onClick={() => editorChainFocus().toggleUnderline().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('کلمه نادیده', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              'S',
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<StrikethroughOutlinedIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_STRIKE)}
              disabled={!editorCanChainFocus().toggleStrike().run()}
              onClick={() => editorChainFocus().toggleStrike().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('کلمه برجسته', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              'H',
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<HighlightOutlinedIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_HIGHLIGHT)}
              disabled={!editorCanChainFocus().toggleHighlight().run()}
              onClick={() => editorChainFocus().toggleHighlight().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('بالا‌نویس', [SHORTCUT_COMMAND, '.'])}
          >
            <Button
              {...defaultButtonProps}
              icon={<SuperscriptIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_SUPERSCRIPT)}
              disabled={!editorCanChainFocus().toggleSuperscript().run()}
              onClick={() => editorChainFocus().toggleSuperscript().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('پایین‌نویس', [SHORTCUT_COMMAND, ','])}
          >
            <Button
              {...defaultButtonProps}
              icon={<SubscriptIcon />}
              data-expanded={isInlineTextExpand}
              data-active={editor.isActive(TOOLBAR_SUBSCRIPT)}
              disabled={!editorCanChainFocus().toggleSubscript().run()}
              onClick={() => editorChainFocus().toggleSubscript().run()}
            />
          </Tooltip>
          <ColorPicker
            defaultValue='var(--ant-color-text-base)'
            value={editor.getAttributes(TOOLBAR_TEXT_STYLE).color}
            onChange={(_, hex) => editorChainFocus().setColor(hex).run()}
          >
            <Tooltip title={generateTooltipTitle('رنگ')}>
              <Button
                {...defaultButtonProps}
                icon={<FontColorsOutlinedIcon />}
                data-expanded={isInlineTextExpand}
                data-active={editor.isActive(TOOLBAR_TEXT_STYLE)}
                disabled={true}
              />
            </Tooltip>
          </ColorPicker>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <Tooltip
            title={generateTooltipTitle('لیست مرتب', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              7,
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<OrderedListOutlinedIcon />}
              data-expanded={isListOptionsExpand}
              data-active={editor.isActive(TOOLBAR_ORDERED_LIST)}
              disabled={!editorCanChainFocus().toggleOrderedList().run()}
              onClick={() => editorChainFocus().toggleOrderedList().run()}
            />
          </Tooltip>
          <React.Fragment>
            <Button
              {...defaultButtonProps}
              type='link'
              data-control-expand='list-options'
              data-control-show={!isListOptionsExpand}
              icon={<EllipsisOutlinedIcon />}
              onClick={toggleListOptionsExpand}
            />
          </React.Fragment>
          <Tooltip
            title={generateTooltipTitle('لیست نامرتب', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              8,
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<UnorderedListOutlinedIcon />}
              data-expanded={isListOptionsExpand}
              data-active={editor.isActive(TOOLBAR_BULLET_LIST)}
              disabled={!editorCanChainFocus().toggleBulletList().run()}
              onClick={() => editorChainFocus().toggleBulletList().run()}
            />
          </Tooltip>
          <Tooltip
            title={generateTooltipTitle('لیست چک‌شده', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              9,
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<CheckSquareOutlinedIcon />}
              data-expanded={isListOptionsExpand}
              data-active={editor.isActive(TOOLBAR_TASK_LIST)}
              disabled={!editorCanChainFocus().toggleTaskList().run()}
              onClick={() => editorChainFocus().toggleTaskList().run()}
            />
          </Tooltip>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <React.Fragment>
            <Tooltip title={generateTooltipTitle('پردازشگر پیوند')}>
              <Button
                {...defaultButtonProps}
                icon={<LinkOutlinedIcon />}
                data-expanded={isMediaContentExpand}
                onClick={toggleLinkModalOpen}
              />
            </Tooltip>
            <Modal
              {...defaultModalProps}
              title={
                <Space direction='vertical' size={0}>
                  <span>افزودن یا ویرایش پیوند (Link)</span>
                  <span className='text-xs font-medium text-[--ant-color-text-description]'>
                    برای ویرایش پیوند‌ها، آن‌ها را انتخاب کرده و وارد ابزار
                    پردازشگر شوید.
                  </span>
                </Space>
              }
              open={isLinkModalOpen}
              modalRender={node => (
                <PrimitiveForm<LinkFieldType>
                  clearOnDestroy
                  form={linkForm}
                  fields={[
                    {
                      name: 'href',
                      value: linkHref,
                    },
                    {
                      name: 'target',
                      value: linkTarget,
                    },
                    {
                      name: 'rels',
                      value: linkRels,
                    },
                  ]}
                  onValuesChange={(
                    changedValues: Partial<LinkFieldType>,
                    values: Partial<LinkFieldType>,
                  ) => {
                    richTextEditorAction({
                      type: 'UPDATE_RICH_TEXT_EDITOR_LINK_ATTRIBUTES',
                      payload: {
                        linkAttributes: {
                          ...linkAttributes,
                          ...changedValues,
                        },
                      },
                    });
                  }}
                  onFinish={({ href, target = null, rels = [] }) => {
                    if (href) {
                      editorChainFocus()
                        .extendMarkRange(TOOLBAR_LINK)
                        .setLink({
                          href,
                          target,
                          rel: rels.length ? rels.join(DEFAULT_SPACE) : null,
                        })
                        .run();
                    } else {
                      editorChainFocus()
                        .extendMarkRange(TOOLBAR_LINK)
                        .unsetLink()
                        .run();
                    }
                    toggleLinkModalOpen();
                    linkForm.resetFields();
                  }}
                >
                  {node}
                </PrimitiveForm>
              )}
              onCancel={toggleLinkModalOpen}
            >
              <Divider />
              <Row>
                <Col span={24}>
                  <Form.Item<LinkFieldType>
                    label='آدرس (Href)'
                    extra='در صورت خالی بودن این فیلد، پیوند پس از تأیید حذف خواهد شد.'
                    name='href'
                    rules={[inputRule.string]}
                  >
                    <Input.Search
                      placeholder='آدرس پیوند را وارد کنید'
                      classNames={{ input: 'text-right [direction:ltr]' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item<LinkFieldType> label='نوع (Target)' name='target'>
                    <Select
                      allowClear
                      placeholder='نوع پیوند را انتخاب کنید'
                      options={getObjectValues(LINK_TARGET_OPTIONS).map(
                        ({ VALUE: value, LABEL: label }) => ({
                          key: randomId(),
                          value,
                          label,
                        }),
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item<LinkFieldType>
                    label='شناسه‌ها (Rel)'
                    extra='می‌توانید مقادیر دلخواه خود را با فشردن کلید Enter یا Space اضافه کنید.'
                    name='rels'
                  >
                    <Select
                      allowClear
                      mode='tags'
                      placeholder='شناسه‌های پیوند را انتخاب کنید'
                      tokenSeparators={[DEFAULT_SPACE]}
                      options={linkRelOptions}
                      optionRender={defaultSelectProps['optionRender']}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </Modal>
          </React.Fragment>
          <React.Fragment>
            <Button
              {...defaultButtonProps}
              type='link'
              data-control-expand='media-content'
              data-control-show={!isMediaContentExpand}
              icon={<EllipsisOutlinedIcon />}
              onClick={toggleMediaContentExpand}
            />
          </React.Fragment>
          <React.Fragment>
            <Tooltip title={generateTooltipTitle('پردازشگر تصویر')}>
              <Button
                {...defaultButtonProps}
                icon={<PictureOutlinedIcon />}
                data-expanded={isMediaContentExpand}
                onClick={toggleImageModalOpen}
              />
            </Tooltip>
            <Modal
              {...defaultModalProps}
              title={
                <Space direction='vertical' size={0}>
                  <span>افزودن یا ویرایش تصویر</span>
                  <span className='text-xs font-medium text-[--ant-color-text-description]'>
                    برای ویرایش تصویرها، آن‌ها را انتخاب کرده و وارد ابزار
                    پردازشگر شوید.
                  </span>
                </Space>
              }
              open={isImageModalOpen}
              modalRender={node => (
                <PrimitiveForm<ImageFieldType>
                  clearOnDestroy
                  form={imageForm}
                  fields={[
                    {
                      name: 'width',
                      value: imageWidth,
                    },
                    {
                      name: 'height',
                      value: imageHeight,
                    },
                    {
                      name: 'alt',
                      value: imageAlt,
                    },
                  ]}
                  onValuesChange={(
                    changedValues: Partial<ImageFieldType>,
                    values: Partial<ImageFieldType>,
                  ) => {
                    const { fileInfo } = changedValues;
                    const { alt } = values;

                    if (fileInfo) {
                      const {
                        file: { response: fileResponse, status: fileStatus },
                      } = fileInfo;
                      const { id: fileResponseId, name: fileResponseName } =
                        fileResponse ?? {};

                      const isUploadSuccessful = fileStatus === 'done';
                      const { id, name } = {
                        id: isUploadSuccessful ? fileResponseId : undefined,
                        name: isUploadSuccessful ? fileResponseName : undefined,
                      };

                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_IMAGE_ATTRIBUTES',
                        payload: {
                          imageAttributes: {
                            ...imageAttributes,
                            id,
                            name,
                            alt: alt || name,
                          },
                        },
                      });
                      (
                        ['fileInfo', 'width', 'height', 'alt'] satisfies Array<
                          keyof ImageFieldType
                        >
                      ).forEach(value => {
                        imageForm.setFields([
                          {
                            name: value,
                            errors: [],
                          },
                        ]);
                      });
                    } else {
                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_IMAGE_ATTRIBUTES',
                        payload: {
                          imageAttributes: {
                            ...imageAttributes,
                            ...changedValues,
                          },
                        },
                      });
                    }
                  }}
                  onFinish={({ width, height, alt }) => {
                    if (hasImagePreview) {
                      editorChainFocus()
                        .setImage({
                          src:
                            imageSrc ||
                            generateDownloadFile(
                              ownerType,
                              imageId!,
                              width,
                              height,
                            ),
                          alt,
                        })
                        .run();
                      toggleImageModalOpen();
                      imageForm.resetFields();
                    }
                  }}
                >
                  {node}
                </PrimitiveForm>
              )}
              onCancel={toggleImageModalOpen}
            >
              <Divider />
              <Row>
                <Col span={24}>
                  <Form.Item<ImageFieldType>
                    label='تصویر'
                    extra={`فایل‌هایی با حجم بیش از ${formatFileLocaleUnitSize(maxImageUploadSize)} بارگذاری نمی‌شوند.`}
                    name='fileInfo'
                    valuePropName='file'
                    rules={!imageSrc ? [inputRule.required] : undefined}
                  >
                    <AntdUpload
                      ownerType={ownerType}
                      maxSize={maxImageUploadSize}
                      disabled={!!imageSrc}
                      fileList={
                        hasImagePreview
                          ? [
                              {
                                uid: imageSrc || imageId!,
                                name: imageName ?? 'تصویر بارگذاری‌شده',
                                status: 'done',
                                url:
                                  imageSrc ||
                                  generateDownloadFile(ownerType, imageId!),
                              },
                            ]
                          : undefined
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item<ImageFieldType>
                    label='متن جایگزین (Alt)'
                    name='alt'
                    rules={[inputRule.required, inputRule.string]}
                  >
                    <Input placeholder='متن جایگزین تصویر را وارد کنید' />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:pe-2'>
                  <Form.Item<ImageFieldType>
                    label='طول (Width)'
                    name='width'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber
                      placeholder='طول تصویر را وارد کنید'
                      disabled={!!imageSrc}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:ps-2'>
                  <Form.Item<ImageFieldType>
                    label='عرض (Height)'
                    name='height'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber
                      placeholder='عرض تصویر را وارد کنید'
                      disabled={!!imageSrc}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </Modal>
          </React.Fragment>
          <React.Fragment>
            <Tooltip title={generateTooltipTitle('پردازشگر ویدیو')}>
              <Button
                {...defaultButtonProps}
                icon={<PlaySquareOutlinedIcon />}
                data-expanded={isMediaContentExpand}
                onClick={toggleVideoModalOpen}
              />
            </Tooltip>
            <Modal
              {...defaultModalProps}
              title='افزودن ویدیو'
              open={isVideoModalOpen}
              modalRender={node => (
                <PrimitiveForm<VideoFieldType>
                  clearOnDestroy
                  form={videoForm}
                  fields={[
                    {
                      name: 'width',
                      value: videoWidth,
                    },
                    {
                      name: 'height',
                      value: videoHeight,
                    },
                  ]}
                  onValuesChange={(
                    changedValues: Partial<VideoFieldType>,
                    values: Partial<VideoFieldType>,
                  ) => {
                    const { fileInfo } = changedValues;

                    if (fileInfo) {
                      const {
                        file: { response: fileResponse, status: fileStatus },
                      } = fileInfo;
                      const { id: fileResponseId, name: fileResponseName } =
                        fileResponse ?? {};

                      const isUploadSuccessful = fileStatus === 'done';
                      const { id, name } = {
                        id: isUploadSuccessful ? fileResponseId : undefined,
                        name: isUploadSuccessful ? fileResponseName : undefined,
                      };

                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_VIDEO_ATTRIBUTES',
                        payload: {
                          videoAttributes: {
                            ...videoAttributes,
                            id,
                            name,
                          },
                        },
                      });
                    } else {
                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_VIDEO_ATTRIBUTES',
                        payload: {
                          videoAttributes: {
                            ...videoAttributes,
                            ...changedValues,
                          },
                        },
                      });
                    }
                  }}
                  onFinish={({ width, height }) => {
                    if (hasVideoPreview) {
                      editorChainFocus()
                        .setVideo({
                          src:
                            videoSrc ||
                            generateDownloadFile(
                              ownerType,
                              videoId!,
                              width,
                              height,
                            ),
                          width,
                          height,
                          ...(videoId
                            ? {
                                poster: generateDownloadThumbnail(
                                  ownerType,
                                  videoId,
                                  width,
                                  height,
                                ),
                              }
                            : null),
                        })
                        .run();
                      toggleVideoModalOpen();
                      videoForm.resetFields();
                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_VIDEO_ATTRIBUTES',
                        payload: {
                          videoAttributes: {},
                        },
                      });
                    }
                  }}
                >
                  {node}
                </PrimitiveForm>
              )}
              onCancel={toggleVideoModalOpen}
            >
              <Divider />
              <Row>
                <Col span={24}>
                  <Form.Item<VideoFieldType>
                    label='ویدیو'
                    extra={`فایل‌هایی با حجم بیش از ${formatFileLocaleUnitSize(maxVideoUploadSize)} بارگذاری نمی‌شوند.`}
                    name='fileInfo'
                    valuePropName='file'
                    rules={!videoSrc ? [inputRule.required] : undefined}
                  >
                    <AntdUpload
                      withVideos
                      withPictures={false}
                      ownerType={ownerType}
                      maxSize={maxVideoUploadSize}
                      disabled={!!videoSrc}
                      fileList={
                        hasVideoPreview
                          ? [
                              {
                                uid: videoSrc || videoId!,
                                name: videoName ?? 'ویدیو بارگذاری‌شده',
                                status: 'done',
                                url:
                                  videoSrc ||
                                  generateDownloadFile(ownerType, videoId!),
                              },
                            ]
                          : undefined
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:pe-2'>
                  <Form.Item<VideoFieldType>
                    label='طول (Width)'
                    name='width'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber
                      placeholder='طول ویدیو را وارد کنید'
                      disabled={!!videoSrc}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:ps-2'>
                  <Form.Item<VideoFieldType>
                    label='عرض (Height)'
                    name='height'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber
                      placeholder='عرض ویدیو را وارد کنید'
                      disabled={!!videoSrc}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </Modal>
          </React.Fragment>
          <React.Fragment>
            <Tooltip title={generateTooltipTitle('پردازشگر صدا')}>
              <Button
                {...defaultButtonProps}
                icon={<PlayCircleOutlinedIcon />}
                data-expanded={isMediaContentExpand}
                onClick={toggleAudioModalOpen}
              />
            </Tooltip>
            <Modal
              {...defaultModalProps}
              title='افزودن صدا'
              open={isAudioModalOpen}
              modalRender={node => (
                <PrimitiveForm<AudioFieldType>
                  clearOnDestroy
                  form={audioForm}
                  onValuesChange={(
                    changedValues: Partial<AudioFieldType>,
                    values: Partial<AudioFieldType>,
                  ) => {
                    const { fileInfo } = changedValues;

                    if (fileInfo) {
                      const {
                        file: { response: fileResponse, status: fileStatus },
                      } = fileInfo;
                      const { id: fileResponseId, name: fileResponseName } =
                        fileResponse ?? {};

                      const isUploadSuccessful = fileStatus === 'done';
                      const { id, name } = {
                        id: isUploadSuccessful ? fileResponseId : undefined,
                        name: isUploadSuccessful ? fileResponseName : undefined,
                      };

                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_AUDIO_ATTRIBUTES',
                        payload: {
                          audioAttributes: {
                            ...audioAttributes,
                            id,
                            name,
                          },
                        },
                      });
                    } else {
                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_AUDIO_ATTRIBUTES',
                        payload: {
                          audioAttributes: {
                            ...audioAttributes,
                            ...changedValues,
                          },
                        },
                      });
                    }
                  }}
                  onFinish={() => {
                    if (hasAudioPreview) {
                      editorChainFocus()
                        .setAudio({
                          src:
                            audioSrc ||
                            generateDownloadFile(
                              ownerType,
                              audioId!,
                              AUDIO_SIZE_WIDTH,
                              AUDIO_SIZE_HEIGHT,
                            ),
                        })
                        .run();
                      toggleAudioModalOpen();
                      audioForm.resetFields();
                      richTextEditorAction({
                        type: 'UPDATE_RICH_TEXT_EDITOR_AUDIO_ATTRIBUTES',
                        payload: {
                          audioAttributes: {},
                        },
                      });
                    }
                  }}
                >
                  {node}
                </PrimitiveForm>
              )}
              onCancel={toggleAudioModalOpen}
            >
              <Divider />
              <Row>
                <Col span={24}>
                  <Form.Item<AudioFieldType>
                    label='صدا'
                    extra={`فایل‌هایی با حجم بیش از ${formatFileLocaleUnitSize(maxAudioUploadSize)} بارگذاری نمی‌شوند.`}
                    name='fileInfo'
                    valuePropName='file'
                    rules={!audioSrc ? [inputRule.required] : undefined}
                  >
                    <AntdUpload
                      withAudios
                      withPictures={false}
                      ownerType={ownerType}
                      maxSize={maxAudioUploadSize}
                      disabled={!!audioSrc}
                      fileList={
                        hasAudioPreview
                          ? [
                              {
                                uid: audioSrc || audioId!,
                                name: audioName ?? 'صدا بارگذاری‌شده',
                                status: 'done',
                                url:
                                  audioSrc ||
                                  generateDownloadFile(ownerType, audioId!),
                              },
                            ]
                          : undefined
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </Modal>
          </React.Fragment>
          <React.Fragment>
            <Tooltip title={generateTooltipTitle('پردازشگر چارچوب')}>
              <Button
                {...defaultButtonProps}
                icon={<GatewayOutlinedIcon />}
                data-expanded={isMediaContentExpand}
                onClick={toggleIframeModalOpen}
              />
            </Tooltip>
            <Modal
              {...defaultModalProps}
              title='افزودن چارچوب (iFrame)'
              open={isIframeModalOpen}
              modalRender={node => (
                <PrimitiveForm<IframeFieldType>
                  clearOnDestroy
                  form={iframeForm}
                  fields={[
                    {
                      name: 'src',
                      value: iframeSrc,
                    },
                    {
                      name: 'width',
                      value: iframeWidth,
                    },
                    {
                      name: 'height',
                      value: iframeHeight,
                    },
                  ]}
                  onValuesChange={(
                    changedValues: Partial<IframeFieldType>,
                    values: Partial<IframeFieldType>,
                  ) => {
                    richTextEditorAction({
                      type: 'UPDATE_RICH_TEXT_EDITOR_IFRAME_ATTRIBUTES',
                      payload: {
                        iframeAttributes: {
                          ...iframeAttributes,
                          ...changedValues,
                        },
                      },
                    });
                  }}
                  onFinish={({ src, width, height }) => {
                    editorChainFocus()
                      .setIframe({
                        src,
                        width,
                        height,
                      })
                      .run();
                    toggleIframeModalOpen();
                    iframeForm.resetFields();
                    richTextEditorAction({
                      type: 'UPDATE_RICH_TEXT_EDITOR_IFRAME_ATTRIBUTES',
                      payload: {
                        iframeAttributes: {},
                      },
                    });
                  }}
                >
                  {node}
                </PrimitiveForm>
              )}
              onCancel={toggleIframeModalOpen}
            >
              <Divider />
              <Row>
                <Col span={24}>
                  <Form.Item<IframeFieldType>
                    label='آدرس (Src)'
                    extra='مقدار src را از تگ iframe در بخش کد embed کپی کنید.'
                    name='src'
                    rules={[inputRule.required, inputRule.string]}
                  >
                    <Input.Search
                      placeholder='آدرس چارچوب را وارد کنید'
                      classNames={{ input: 'text-right [direction:ltr]' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:pe-2'>
                  <Form.Item<IframeFieldType>
                    label='طول (Width)'
                    name='width'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber placeholder='طول چارچوب را وارد کنید' />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12} className='lg:ps-2'>
                  <Form.Item<IframeFieldType>
                    label='عرض (Height)'
                    name='height'
                    rules={[inputRule.required, inputRule.digit]}
                  >
                    <DecimalInputNumber placeholder='عرض چارچوب را وارد کنید' />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </Modal>
          </React.Fragment>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <Tooltip title={generateTooltipTitle('ساخت جدول جدید')}>
            <Button
              {...defaultButtonProps}
              icon={<TableIcon />}
              data-expanded={isTableItemsExpand}
              data-active={editor.isActive(TOOLBAR_TABLE)}
              onClick={() =>
                editorChainFocus()
                  .insertTable({ rows: 4, cols: 4, withHeaderRow: true })
                  .run()
              }
            />
          </Tooltip>
          <React.Fragment>
            <Button
              {...defaultButtonProps}
              type='link'
              data-control-expand='table-items'
              data-control-show={!isTableItemsExpand}
              icon={<EllipsisOutlinedIcon />}
              onClick={toggleTableItemsExpand}
            />
          </React.Fragment>
          <Tooltip title={generateTooltipTitle('تغییر وضعیت ردیف عنوان')}>
            <Button
              {...defaultButtonProps}
              icon={<TableHeaderRowIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().toggleHeaderRow()}
              onClick={() => editorChainFocus().toggleHeaderRow().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('تغییر وضعیت ستون عنوان')}>
            <Button
              {...defaultButtonProps}
              icon={<TableHeaderColumnIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().toggleHeaderColumn()}
              onClick={() => editorChainFocus().toggleHeaderColumn().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('تغییر وضعیت سلول عنوان')}>
            <Button
              {...defaultButtonProps}
              icon={<TableHeaderCellIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().toggleHeaderCell()}
              onClick={() => editorChainFocus().toggleHeaderCell().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('افزودن ردیف به بالا')}>
            <Button
              {...defaultButtonProps}
              icon={<TableRowInsertBeforeIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().addRowBefore()}
              onClick={() => editorChainFocus().addRowBefore().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('افزودن ردیف به پایین')}>
            <Button
              {...defaultButtonProps}
              icon={<TableRowInsertAfterIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().addRowAfter()}
              onClick={() => editorChainFocus().addRowAfter().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('افزودن ستون به راست')}>
            <Button
              {...defaultButtonProps}
              icon={<TableColumnInsertBeforeIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().addColumnBefore()}
              onClick={() => editorChainFocus().addColumnBefore().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('افزودن ستون به چپ')}>
            <Button
              {...defaultButtonProps}
              icon={<TableColumnInsertAfterIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().addColumnAfter()}
              onClick={() => editorChainFocus().addColumnAfter().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('ادغام سلول‌های انتخاب‌شده')}>
            <Button
              {...defaultButtonProps}
              icon={<TableCellMergeIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().mergeCells()}
              onClick={() => editorChainFocus().mergeCells().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('تقسیم سلول ادغام‌شده')}>
            <Button
              {...defaultButtonProps}
              icon={<TableCellSplitIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().splitCell()}
              onClick={() => editorChainFocus().splitCell().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('حذف ردیف انتخاب‌شده')}>
            <Button
              {...defaultButtonProps}
              icon={<TableRowRemoveIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().deleteRow()}
              onClick={() => editorChainFocus().deleteRow().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('حذف ستون انتخاب‌شده')}>
            <Button
              {...defaultButtonProps}
              icon={<TableColumnRemoveIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().deleteColumn()}
              onClick={() => editorChainFocus().deleteColumn().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('حذف کامل جدول')}>
            <Button
              {...defaultButtonProps}
              icon={<TableRemoveIcon />}
              data-expanded={isTableItemsExpand}
              disabled={!editorCan().deleteTable()}
              onClick={() => editorChainFocus().deleteTable().run()}
            />
          </Tooltip>
        </Flex>
        <Divider type='vertical' />
        <Flex>
          <Tooltip
            title={generateTooltipTitle('نقل قول', [
              SHORTCUT_COMMAND,
              SHORTCUT_SHIFT,
              'B',
            ])}
          >
            <Button
              {...defaultButtonProps}
              icon={<PicRightOutlinedIcon />}
              data-active={editor.isActive(TOOLBAR_BLOCKQUOTE)}
              disabled={!editorCanChainFocus().toggleBlockquote().run()}
              onClick={() => editorChainFocus().toggleBlockquote().run()}
            />
          </Tooltip>
          <Tooltip title={generateTooltipTitle('خط افقی')}>
            <Button
              {...defaultButtonProps}
              icon={<BorderBottomOutlinedIcon />}
              data-active={editor.isActive(TOOLBAR_HORIZONTAL_RULE)}
              onClick={() => editorChainFocus().setHorizontalRule().run()}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Row className='divide-x divide-x-reverse border-y bg-[--ant-color-fill-secondary] *:p-2 *:text-center'>
        <Col span={12}>
          <Space size={4} className='*:font-medium'>
            <Typography.Text>{characters}</Typography.Text>
            <Typography.Text className='text-xs'>نویسه</Typography.Text>
          </Space>
        </Col>
        <Col span={12}>
          <Space size={4} className='*:font-medium'>
            <Typography.Text>{words}</Typography.Text>
            <Typography.Text className='text-xs'>کلمه</Typography.Text>
          </Space>
        </Col>
      </Row>
    </Flex>
  );
});
RichTextEditorToolbar.displayName = 'RichTextEditorToolbar';

type RichTextEditorProps = React.ComponentPropsWithoutRef<'div'> &
  Record<'options', NonNullable<Parameters<typeof useEditor>[0]>> &
  Partial<
    Record<
      'config',
      Partial<
        Record<'starterKit', Partial<StarterKitOptions>> &
          Record<'textStyle', Partial<TextStyleOptions>> &
          Record<'textAlign', Partial<TextAlignOptions>> &
          Record<'color', Partial<ColorOptions>> &
          Record<'underline', Partial<UnderlineOptions>> &
          Record<'highlight', Partial<HighlightOptions>> &
          Record<'subscript', Partial<SubscriptExtensionOptions>> &
          Record<'superscript', Partial<SuperscriptExtensionOptions>> &
          Record<'link', Partial<LinkOptions>> &
          Record<'image', Partial<ImageOptions>> &
          Record<'taskList', Partial<TaskListOptions>> &
          Record<'taskItem', Partial<TaskItemOptions>> &
          Record<'table', Partial<TableOptions>> &
          Record<'tableRow', Partial<TableRowOptions>> &
          Record<'tableHeader', Partial<TableHeaderOptions>> &
          Record<'tableCell', Partial<TableCellOptions>> &
          Record<'characterCount', Partial<CharacterCountOptions>> &
          Record<'placeholder', Partial<PlaceholderOptions>> &
          Record<'video', Partial<VideoOptions>> &
          Record<'audio', Partial<AudioOptions>> &
          Record<'iframe', Partial<IframeOptions>>
      >
    > &
      Record<'deps', Parameters<typeof useEditor>[1]>
  > &
  Record<'ownerType', FileOwnerType>;

const RichTextEditor = React.forwardRef<
  React.ElementRef<'div'>,
  RichTextEditorProps
>(
  (
    { options = {}, config = {}, deps, ownerType, className, ...props },
    ref,
  ) => {
    const {
      shouldRerenderOnTransaction = true,
      extensions = [],
      editorProps = {
        attributes: {
          class:
            'max-h-[36rem] min-h-[18rem] resize-y overflow-y-auto p-3 caret-[--ant-color-text] focus-visible:outline focus-visible:outline-1 focus-visible:outline-[--ant-color-primary-hover]',
        },
      },
      ...otherOptions
    } = options;
    const {
      starterKit = {
        heading: {
          levels: [2, 3, 4, 5, 6], // ❌ omit level 1
        },
        code: false,
        codeBlock: false,
      },
      textStyle,
      textAlign = {
        defaultAlignment: 'right',
        types: ['heading', 'paragraph'],
      },
      color = { types: [TextStyle.name, ListItem.name] },
      underline,
      highlight,
      subscript,
      superscript,
      link = {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: null,
          rel: null,
        },
      },
      image,
      taskList,
      taskItem,
      table,
      tableRow,
      tableHeader,
      tableCell,
      characterCount,
      placeholder,
      video,
      audio,
      iframe,
    } = config;

    const TableNode = Table.extend({
      addNodeView() {
        return () => {
          const wrapper = document.createElement('div');
          const table = document.createElement('table');
          wrapper.classList.add('table-wrapper');
          wrapper.appendChild(table);

          return {
            dom: wrapper,
            contentDOM: table,
          };
        };
      },
      renderHTML({ HTMLAttributes }) {
        return [
          'div',
          { class: 'table-wrapper' },
          ['table', mergeAttributes(HTMLAttributes), 0],
        ];
      },
    });
    const editor = useEditor(
      {
        shouldRerenderOnTransaction,
        extensions: [
          StarterKit.configure(starterKit),
          TextStyle.configure(textStyle),
          TextAlign.configure(textAlign),
          Color.configure(color),
          Underline.configure(underline),
          Highlight.configure(highlight),
          Subscript.configure(subscript),
          Superscript.configure(superscript),
          Link.configure(link),
          Image.configure(image),
          TaskList.configure(taskList),
          TaskItem.configure(taskItem),
          TableNode.configure(table),
          TableRow.configure(tableRow),
          TableHeader.configure(tableHeader),
          TableCell.configure(tableCell),
          CharacterCount.configure(characterCount),
          Placeholder.configure(placeholder),
          Video.configure(video),
          Audio.configure(audio),
          Iframe.configure(iframe),
          ...extensions,
        ],
        editorProps,
        ...otherOptions,
      },
      deps,
    );

    if (!editor) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'rounded border xl:max-w-[calc(100vmax-40vw-5rem)]',
          className,
        )}
        {...props}
      >
        <RichTextEditorContext>
          <RichTextEditorToolbar editor={editor} ownerType={ownerType} />
          <RichTextEditorContent editor={editor} />
        </RichTextEditorContext>
      </div>
    );
  },
);
RichTextEditor.displayName = 'RichTextEditor';

export { RichTextEditor, RichTextEditorToolbar };
